import IconButton from '@material-ui/core/IconButton'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiX } from 'react-icons/bi'

import api from 'src/api'
import { SmartPatrolOffenseCreate, SmartPatrolOffenseCreate_DeviceTypeEnum } from 'src/api/gen'
import { getApiErrorMessage } from 'src/api/helpers'
import { getEnumEntries } from 'src/commons/filters/utils'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { JurisdictionBoundary } from 'src/commons/types'
import ErrorSnackbar, { SuccessSnackbar } from 'src/components/Snackbar'
import { useAgencySettings } from 'src/layout/AgencySettings'
import { useProviders, useUserProfile } from 'src/layout/hooks'
import LocationPicker, { ILatLon } from 'src/muiv5/components/commons/LocationPicker'

/** Vehicle fields to pre-fill and lock when reporting an offense for a specific vehicle from the live map. */
export interface IOffenseVehiclePrefill {
  deviceType: string
  deviceVin: string
  location: ILatLon
  providerId: string
}

export interface ISmartPatrolOffenseDialogProps {
  /** Jurisdiction boundary to overlay on the picker. Passed by the mobile layout to avoid a second fetch. */
  boundary?: JurisdictionBoundary
  onClose: () => void
  /** Called after an offense has been successfully created (e.g. to refresh the list). */
  onCreated?: () => void
  open: boolean
  /** Pre-fill the location from the device GPS — enabled on the mobile layout. */
  prefillFromGeolocation?: boolean
  /** When set, lock the vehicle fields to this vehicle; only violation type and comment stay editable. */
  vehicle?: IOffenseVehiclePrefill
}

export default function SmartPatrolOffenseDialog({
  open,
  onClose,
  onCreated,
  boundary,
  prefillFromGeolocation = false,
  vehicle,
}: ISmartPatrolOffenseDialogProps) {
  const { t } = useTranslation()
  const { activeProviders } = useProviders()
  const { jurisdiction } = useUserProfile()
  const { vehicle_types } = useAgencySettings()

  const [offenseType, setOffenseType] = useState('')
  const [providerId, setProviderId] = useState('')
  const [deviceVin, setDeviceVin] = useState('')
  const [deviceType, setDeviceType] = useState('')
  const [comment, setComment] = useState('')
  const [location, setLocation] = useState<ILatLon | null>(null)
  const [fetchedBoundary, setFetchedBoundary] = useState<JurisdictionBoundary | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successOpen, setSuccessOpen] = useState(false)

  // The mobile layout passes the jurisdiction boundary in; otherwise fetch it when the dialog opens.
  useEffect(() => {
    if (boundary || !open) {
      return undefined
    }
    const abortController = new AbortController()
    api.jurisdiction
      .jurisdictionBoundaryList({ options: { signal: abortController.signal } })
      .then((response) => setFetchedBoundary(response as unknown as JurisdictionBoundary))
      .catch(() => {
        // The boundary is a visual aid only; ignore failures.
      })
    return () => abortController.abort()
  }, [open, boundary])

  // When opened for a specific vehicle, pre-fill and lock its fields; only the violation type and
  // comment stay editable. reset() clears these again on close.
  const lockVehicleFields = Boolean(vehicle)
  useEffect(() => {
    if (open && vehicle) {
      setProviderId(vehicle.providerId)
      setDeviceVin(vehicle.deviceVin)
      setDeviceType(vehicle.deviceType)
      setLocation(vehicle.location)
    }
  }, [open, vehicle])

  const vehicleTypeEntries = getEnumEntries(vehicle_types, enumsTranslationKeys.DeviceType)

  const isValid = Boolean(offenseType && providerId && deviceVin && deviceType && location)

  function reset() {
    setOffenseType('')
    setProviderId('')
    setDeviceVin('')
    setDeviceType('')
    setComment('')
    setLocation(null)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleSubmit() {
    if (!isValid || !location) {
      return
    }
    const data: SmartPatrolOffenseCreate = {
      offense_type: offenseType,
      provider_id: providerId,
      device_vin: deviceVin,
      device_type: deviceType as SmartPatrolOffenseCreate_DeviceTypeEnum,
      comment: comment || undefined,
      lat: location.lat,
      lon: location.lon,
    }
    setSubmitting(true)
    try {
      await api.smartPatrol.smartPatrolOffensesCreate({ data })
      setSuccessOpen(true)
      onCreated?.()
      handleClose()
    } catch (ex) {
      console.error(ex)
      setErrorMessage(getApiErrorMessage(ex) ?? t('smart_patrol.create_offense.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <Card sx={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
          <CardHeader
            title={t('smart_patrol.create_offense.title')}
            action={
              <IconButton onClick={handleClose}>
                <BiX />
              </IconButton>
            }
          />
          <CardContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Stack gap={2.5}>
              <TextField
                select
                required
                fullWidth
                id="smart-patrol-offense-type"
                label={t('smart_patrol.violation_type')}
                value={offenseType}
                onChange={(event) => setOffenseType(event.target.value)}
              >
                {jurisdiction.smart_patrol_violation_types.map((violation) => (
                  <MenuItem key={violation.id} value={violation.id}>
                    {violation.localized_name || violation.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                required
                fullWidth
                disabled={lockVehicleFields}
                id="smart-patrol-provider"
                label={t('smart_patrol.provider')}
                value={providerId}
                onChange={(event) => setProviderId(event.target.value)}
              >
                {activeProviders.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                required
                fullWidth
                disabled={lockVehicleFields}
                id="smart-patrol-vin"
                label="VIN"
                value={deviceVin}
                onChange={(event) => setDeviceVin(event.target.value)}
              />

              <TextField
                select
                required
                fullWidth
                disabled={lockVehicleFields}
                id="smart-patrol-vehicle-type"
                label={t('smart_patrol.vehicle_type')}
                value={deviceType}
                onChange={(event) => setDeviceType(event.target.value)}
              >
                {vehicleTypeEntries.map((entry) => (
                  <MenuItem key={entry.id} value={entry.id}>
                    {entry.label}
                  </MenuItem>
                ))}
              </TextField>

              <Stack gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {t('smart_patrol.create_offense.location')}
                </Typography>
                <LocationPicker
                  value={location}
                  onChange={setLocation}
                  boundary={boundary ?? fetchedBoundary}
                  prefillFromGeolocation={prefillFromGeolocation && !lockVehicleFields}
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={2}
                id="smart-patrol-comment"
                label={t('smart_patrol.comment')}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
            <Button onClick={handleClose} color="inherit">
              {t('button.cancel')}
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!isValid || submitting}>
              {t('smart_patrol.create_offense.submit')}
            </Button>
          </CardActions>
        </Card>
      </Dialog>
      <SuccessSnackbar
        open={successOpen}
        message={t('smart_patrol.create_offense.success')}
        onClose={() => setSuccessOpen(false)}
      />
      <ErrorSnackbar open={Boolean(errorMessage)} message={errorMessage ?? ''} onClose={() => setErrorMessage(null)} />
    </>
  )
}
